import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, ShieldAlert, Loader2, RefreshCw, UserX, Ghost, Archive, ChevronDown,
} from "lucide-react";

/**
 * Account health (round 8, W6).
 *
 * Three admin endpoints have worked since round 3 with no interface at all. This
 * surfaces the one that matters and the single useful line from each of the other two.
 *
 * GET /admin/auth/audit is the one that matters. It finds users who exist in Mongo but
 * not in Firebase, and since the frontend authenticates through Firebase, an INVITED
 * user in that state cannot sign in at all. That is not a theoretical failure: it is
 * the round 3 bug that hit Robin's own account, and it was invisible because the
 * endpoint had no page. The panel is deliberately quiet when healthy, because a warning
 * that is always on screen stops being read.
 *
 * GET /admin/role-audit mostly duplicates the users table above it. The exception is
 * accounts_without_owner: an advertiser account with nobody who can administer it, a
 * customer-facing problem the table cannot show. That one line is taken; the rest is
 * not worth a page.
 *
 * GET /admin/archives returns rows, not counts, and browsing archives properly is its
 * own screen. What is shown here is the count, which answers the one question a delete
 * raises: did it actually archive. Anything more is recorded as not built.
 *
 * MASTER_ADMIN only, because the auth audit is. A BOOMA_ADMIN sees the rest.
 */
export default function AuthHealthPanel() {
  const { token, user } = useAuth();
  const isMaster = user?.role === "MASTER_ADMIN";

  const [audit, setAudit] = useState(null);
  const [ownerless, setOwnerless] = useState(null);
  const [archives, setArchives] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };

    // Three independent calls, each allowed to fail on its own. The auth audit talks to
    // the Firebase Admin SDK and is the slowest and most likely to error; it must not
    // take the other two down with it, and none of them may take the page down.
    const [auditRes, roleRes, archiveRes] = await Promise.allSettled([
      isMaster ? axios.get(`${API}/admin/auth/audit`, { headers }) : Promise.reject(),
      axios.get(`${API}/admin/role-audit`, { headers }),
      axios.get(`${API}/admin/archives`, { headers }),
    ]);

    setAudit(auditRes.status === "fulfilled" ? auditRes.value.data : null);
    setOwnerless(
      roleRes.status === "fulfilled"
        ? roleRes.value.data?.accounts_without_owner || []
        : null
    );
    setArchives(archiveRes.status === "fulfilled" ? archiveRes.value.data : null);
    setLoading(false);
  }, [token, isMaster]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking account health
        </CardContent>
      </Card>
    );
  }

  const summary = audit?.summary;
  const brokenInvitees = audit?.broken_invitees || [];
  const ghosts = audit?.orphaned_firebase || [];
  const legacy = (audit?.orphaned_mongodb || []).filter((o) => !o.was_invited);
  const noOwner = ownerless || [];

  const archiveCount = archives
    ? (archives.users?.length || 0) + (archives.accounts?.length || 0) + (archives.campaigns?.length || 0)
    : null;

  // Healthy means: nobody locked out, no ghost, no account without an owner. A legacy
  // JWT user is not a fault, so it does not count against this.
  const healthy =
    (!summary || summary.health_status === "HEALTHY") && noOwner.length === 0;

  return (
    <Card className={healthy ? "bg-card border-border" : "border-amber-500/40 bg-amber-500/5"}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {healthy ? (
                <ShieldCheck className="h-4 w-4 text-green-500" />
              ) : (
                <ShieldAlert className="h-4 w-4 text-amber-500" />
              )}
              Account health
            </CardTitle>
            <CardDescription>
              {healthy
                ? "Everyone who should be able to sign in can, and every advertiser account has an owner."
                : "Something here stops a real person using the system. Details below."}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="auth-health-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Recheck
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* The one that locks people out. Listed by name, because the fix is per person. */}
        {brokenInvitees.length > 0 && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2">
            <p className="text-sm font-semibold text-red-500 flex items-center gap-2">
              <UserX className="h-4 w-4" />
              {brokenInvitees.length} invited {brokenInvitees.length === 1 ? "user" : "users"} cannot sign in
            </p>
            <p className="text-xs text-muted-foreground">
              They have a record in the CRM but none in Firebase, so the login page will
              reject them whatever password they use. The invite has to be reissued.
            </p>
            <div className="space-y-1">
              {brokenInvitees.map((entry) => (
                <div key={entry.email} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs">{entry.email}</span>
                  {entry.role && <Badge variant="outline" className="text-[10px]">{entry.role}</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* An advertiser account nobody can administer. */}
        {noOwner.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
            <p className="text-sm font-semibold text-amber-600">
              {noOwner.length} advertiser {noOwner.length === 1 ? "account has" : "accounts have"} no owner
            </p>
            <p className="text-xs text-muted-foreground">
              Nobody on the account can add users, change billing details or accept
              terms. Promote one of their users, or invite one.
            </p>
            <div className="space-y-1">
              {/* The endpoint returns {id, name}, not the account shape used elsewhere. */}
              {noOwner.slice(0, 10).map((entry, index) => (
                <div key={entry.id || index} className="text-sm">
                  {entry.name || entry.id}
                </div>
              ))}
              {noOwner.length > 10 && (
                <p className="text-xs text-muted-foreground">
                  and {noOwner.length - 10} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Ghosts can authenticate and then land nowhere. A nuisance, not a lockout. */}
        {ghosts.length > 0 && (
          <div className="rounded-lg border border-border p-3 space-y-1">
            <p className="text-sm font-medium flex items-center gap-2">
              <Ghost className="h-4 w-4 text-muted-foreground" />
              {ghosts.length} Firebase {ghosts.length === 1 ? "login has" : "logins have"} no CRM record
            </p>
            <p className="text-xs text-muted-foreground">
              They can pass the login screen and then reach nothing. Usually a deleted
              user whose Firebase account outlived them.
            </p>
            <div className="pt-1 space-y-0.5">
              {ghosts.slice(0, 5).map((entry) => (
                <p key={entry.email} className="font-mono text-xs">{entry.email}</p>
              ))}
              {ghosts.length > 5 && (
                <p className="text-xs text-muted-foreground">and {ghosts.length - 5} more</p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          {summary && (
            <>
              <span className="text-muted-foreground">
                Signed up in both systems <span className="text-foreground font-medium tabular-nums">{summary.synced_count}</span>
              </span>
              {legacy.length > 0 && (
                <span className="text-muted-foreground">
                  Legacy password login <span className="text-foreground font-medium tabular-nums">{legacy.length}</span>
                </span>
              )}
            </>
          )}
          {archiveCount !== null && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="archives-toggle"
            >
              <Archive className="h-3.5 w-3.5" />
              Archived <span className="text-foreground font-medium tabular-nums">{archiveCount}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>

        {expanded && archives && (
          <div className="rounded-lg border border-border p-3 text-sm space-y-1">
            <p className="text-xs text-muted-foreground mb-2">
              What deleting an advertiser or a user actually kept. Read only, and not
              browsable here yet.
            </p>
            {[
              ["Users", archives.users?.length || 0],
              ["Advertiser accounts", archives.accounts?.length || 0],
              ["Campaigns", archives.campaigns?.length || 0],
            ].map(([label, count]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium tabular-nums">{count}</span>
              </div>
            ))}
          </div>
        )}

        {!isMaster && (
          <p className="text-xs text-muted-foreground">
            The Firebase sign-in check is Master Admin only, so it is not included above.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
